import React from 'react';
import './order-confirmation-summary-page.scss';
import OrderConfirmationSummary from '../order-confirmation-summary/order-confirmation-summary';
import { RadioGroup, Radio, Button, Form, Icon } from '@ampol/design-system';
import { useState, useCallback, useEffect } from 'react';
import { currencyFormat } from '../../../../utilities/core-utilities';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrderConfirmationSummaryPageContext } from 'selectors/orderConfirmationSummarySelectors';
import { CashShippingMethods } from '../../../../utilities/constants';
import {
  setShippingMethod,
  setOrderDiscount,
  setAdditionalInformation,
  setShippingWaived,
} from '../../../../actions/cashActions';
import * as yup from 'yup';
import { string, number } from 'yup';
import { debounce } from 'lodash';
import CloseIcon from '../../../../assets/icon-close.svg';
import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';
import Label from 'constants/labels';
import { trackCustomEvent } from 'utilities/app-insights';

type DispatchMethod = {
  code: string;
  baseAmount: number;
};

type CashOrderDefaults = {
  dispatchMethod: string;
  dispatchMethods: DispatchMethod[];
  ampolCashAccountTypeCode: string;
};

type CashOrderCartItem = {
  [key: string]: unknown;
};

type OrderConfirmationSummaryPageProps = {
  cashOrderCartItems: CashOrderCartItem[];
  cashCartSubTotal: number;
  cashOrderDefaults: CashOrderDefaults;
  assignedGrandfatherClientNumber: string;
  formId: string;
  isAdmin: boolean;
  onPlaceOrder: () => void;
  orderNumber?: string;
};

type AdditionalInformationValues = {
  requestorName?: string;
  requestorPhone?: string;
  caseNumber?: string;
  purchaseOrderNumber?: string;
};

type DiscountFormValues = {
  discount: number | string | null;
};

const OrderConfirmationSummaryPage: React.FC<OrderConfirmationSummaryPageProps> = ({
  cashOrderCartItems,
  cashCartSubTotal,
  cashOrderDefaults,
  assignedGrandfatherClientNumber,
  formId,
  isAdmin,
  onPlaceOrder,
  orderNumber,
}) => {
  const dispatch = useDispatch();

  const {
    cartShippingMethod,
    cartShippingCost,
    isFeeWaiveOverride,
    cartDiscount,
    loggedInUser,
    assignedAccountNumber,
    cartRequestedName,
    cartRequestedPhone,
    cartCaseNumber,
    cartPurchaseOrderNumber,
    digitalCartOnly,
  } = useSelector(selectOrderConfirmationSummaryPageContext);

  const [option, setOption] = useState(cartShippingMethod || cashOrderDefaults.dispatchMethod || '');

  useEffect(() => {
    if (digitalCartOnly) {
      const shippingMethod = cashOrderDefaults.dispatchMethods.find((method) => method.code === '18');
      dispatch(setShippingMethod(shippingMethod?.code, shippingMethod?.baseAmount || 0));
    } else {
      const shippingCode =
        cartShippingMethod && cartShippingMethod !== '18'
          ? cartShippingMethod
          : cashOrderDefaults.ampolCashAccountTypeCode === 'INT-BUS' && isAdmin
            ? '17'
            : cashOrderDefaults.dispatchMethod;
      shippingCode &&
        dispatch(
          setShippingMethod(
            shippingCode,
            cashOrderDefaults.dispatchMethods.find((method) => method.code === shippingCode)?.baseAmount || 0
          )
        );
    }
  }, [digitalCartOnly]);

  const debouncedSetExtraInfo = useCallback(
    debounce((values: AdditionalInformationValues) => {
      dispatch(
        setAdditionalInformation(
          values?.requestorName,
          values?.requestorPhone,
          values?.caseNumber,
          values?.purchaseOrderNumber?.toUpperCase()
        )
      );
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetExtraInfo.cancel();
    };
  }, [debouncedSetExtraInfo]);

  const extraInfoDefaultValues = {
    requestorName: cartRequestedName,
    requestorPhone: cartRequestedPhone,
    caseNumber: cartCaseNumber,
    purchaseOrderNumber: cartPurchaseOrderNumber,
  };

  const validateMaxTwoDecimals = (val: number | string | null | undefined) => {
    return val ? Regex.CASH_DISCOUNT.test(String(val)) : true;
  };

  const discountValidationSchema = yup.object().shape({
    discount: number()
      .typeError(Validation.DISCOUNT)
      .min(0, Validation.DISCOUNT_MIN)
      .max(100, Validation.DISCOUNT_MAX)
      .nullable(true)
      .transform((_, val) => (val ? Number(val) : null))
      .test('is-valid-decimal', Validation.DISCOUNT, validateMaxTwoDecimals),
  });

  const extraInfoValidationSchema = yup.object().shape({
    requestorName: string()
      .transform((value) => (value ? value.trim() : value))
      .nullable()
      .matches(Regex.ALPHA_SPACE_HYPHEN, { message: Validation.ALPHA_SPACE_HYPHEN })
      .required(Validation.REQUESTOR_NAME),
    requestorPhone: string()
      .transform((value) => (value ? value.trim() : value))
      .nullable()
      .matches(Regex.MOBILE_LANDLINE, {
        excludeEmptyString: true,
        message: Validation.MOBILE_LANDLINE,
      }),
    caseNumber: string()
      .transform((value) => (value ? value.trim() : value))
      .nullable()
      .matches(Regex.ALPHA_NUM, { message: Validation.ALPHA_NUM })
      .required(Validation.CASE_NUMBER),
    purchaseOrderNumber: string()
      .transform((value) => (value ? value.trim() : value))
      .nullable()
      .max(16, Validation.MAX_CHAR_SIXTEEN)
      .matches(Regex.ALPHA_NUM, Validation.ALPHA_NUM)
      .transform((value, originalValue) => originalValue && originalValue.toUpperCase()),
  });

  const handleRadioChange = (shippingCode: string) => {
    setOption(shippingCode);
    dispatch(
      setShippingMethod(
        shippingCode,
        cashOrderDefaults.dispatchMethods.find((method) => method.code === shippingCode)?.baseAmount || 0
      )
    );
  };

  const calculateTotalCost = () => {
    const shippingTotal = isFeeWaiveOverride ? 0 : cartShippingCost || 0;

    return currencyFormat(
      cashCartSubTotal + shippingTotal - (cartDiscount ? (cashCartSubTotal * cartDiscount) / 100 : 0)
    );
  };

  const submitDiscount = (values: DiscountFormValues) => {
    trackCustomEvent(
      'Click Apply Discount',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to apply discount to cash order.'
    );
    dispatch(setOrderDiscount(values.discount));
  };

  return (
    <>
      <div className='order-summary'>
        <div className='order-summary__confirmation'>
          <OrderConfirmationSummary
            cashOrderCartItems={cashOrderCartItems}
            accountNumber={assignedGrandfatherClientNumber}
            orderNumber={orderNumber}
          />
        </div>
        <div className='order-summary__grid'>
          <div className='order-summary__shipping'>
            {!orderNumber && !digitalCartOnly && (
              <div>
                <div className='order-summary__subtitle'>Select shipping method</div>
                <RadioGroup
                  color={'black'}
                  name={'shippingOptions'}
                  onChange={handleRadioChange}
                  direction={'column'}
                  value={option}
                  className={'order-summary__radio-group'}
                >
                  {cashOrderDefaults.dispatchMethods
                    .filter(
                      (method) =>
                        (method.baseAmount != null && method.baseAmount > 0) || (isAdmin && method.code === '17')
                    )
                    .map((method) => (
                      <React.Fragment key={method.code}>
                        <Radio
                          label={
                            <div className='order-summary__radio'>
                              <div className='order-summary__radio-code'>
                                {(CashShippingMethods as Record<string, string>)[method.code]}
                              </div>
                              <div className='order-summary__radio-price'>{currencyFormat(method.baseAmount || 0)}</div>
                            </div>
                          }
                          value={method.code}
                          tooltip={
                            method.code === '10' && cartShippingMethod === '10' ? Label.CASH_COURIER_TOOLTIP : undefined
                          }
                        />
                      </React.Fragment>
                    ))}
                </RadioGroup>
                {isAdmin && (
                  <div className={'order-summary__waived'}>
                    <Form
                      id={'shipping-waived'}
                      schema={{}}
                      onChange={(val: { isFeeWaiveOverride?: boolean }) =>
                        dispatch(setShippingWaived(val.isFeeWaiveOverride))
                      }
                    >
                      <Form.Checkbox color={'black'} name={'isFeeWaiveOverride'} label={'Shipping waived'} />
                    </Form>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className='order-summary__discount'>
            {isAdmin && !orderNumber && (
              <div>
                <div className='order-summary__subtitle'>Apply discount</div>
                <div className='order-summary__input-field'>
                  <Form
                    id={'discount-form'}
                    schema={discountValidationSchema}
                    defaultValues={{ discount: cartDiscount ?? '' }}
                    onSubmit={(values) => submitDiscount(values)}
                    HTMLFormProps={{ noValidate: true }}
                  >
                    <Form.Input
                      label={'Discount'}
                      labelType={'hidden'}
                      name={'discount'}
                      type={'number'}
                      placeholder={'Discount %'}
                      step='any'
                      onKeyDown={(evt) => ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()}
                      inputSuffix={
                        cartDiscount ? (
                          <div style={{ cursor: 'pointer' }} onClick={() => submitDiscount({ discount: '' })}>
                            <Icon url={CloseIcon} />
                          </div>
                        ) : undefined
                      }
                    />
                  </Form>
                </div>
                <Button
                  variant='primary'
                  label='Apply'
                  color='blue'
                  className='order-summary__discount-button'
                  type='submit'
                  formId={'discount-form'}
                />
              </div>
            )}
          </div>
          <div className='order-summary__cost-summary'>
            <div className='order-summary__subtitle'>
              <span>Sub Total: </span>
              <span className='order-summary__right-align'>{currencyFormat(cashCartSubTotal)}</span>
            </div>
            {isAdmin && !!cartDiscount && (
              <div>
                <span>Discount Applied{` (${cartDiscount}%)`}: </span>
                <span className='order-summary__right-align'>
                  {'-' + currencyFormat((cashCartSubTotal * cartDiscount) / 100)}
                </span>
              </div>
            )}
            {!!cartShippingCost && !isFeeWaiveOverride && (
              <div>
                <span>Shipping: </span>
                <span className='order-summary__right-align'>{currencyFormat(cartShippingCost)}</span>
              </div>
            )}
            <div className='order-summary__right-align order-summary__total'>Total: {calculateTotalCost()}</div>
          </div>
        </div>
      </div>

      {!orderNumber && (
        <Form
          id={formId}
          schema={extraInfoValidationSchema}
          defaultValues={extraInfoDefaultValues}
          onChange={debouncedSetExtraInfo}
          onSubmit={onPlaceOrder}
        >
          <div className='order-summary__extra-info-form order-summary__grid'>
            <div className='order-summary__col-span-3 order-summary__subtitle'>Additional Information</div>
            {isAdmin && (
              <>
                <div className='order-summary__input-field'>
                  <Form.Input label={'Requestor name'} name={'requestorName'} />
                </div>
                <div className='order-summary__input-field'>
                  <Form.Input label={'Requestor phone'} name={'requestorPhone'} />
                </div>
                <div className='order-summary__input-field'>
                  <Form.Input label={'Case number'} name={'caseNumber'} />
                </div>
              </>
            )}
            <div className='order-summary__input-field'>
              <Form.Input
                label={'Purchase Order Number'}
                name={'purchaseOrderNumber'}
                className={'order-summary__uppercase'}
              />
            </div>
          </div>
        </Form>
      )}
    </>
  );
};

export default OrderConfirmationSummaryPage;
